/**
 * Notifiche device online: delivery via heartbeat tab leader (~15s).
 * Detection server-side al passaggio pacchetto PTS in store().
 */
import {
    buildNotificationDedupKey,
    releaseNotificationSlot,
    tryAcquireNotificationSlot,
} from './presenceClaim';
import {
    getPreparedQwebNotificationIconUrl,
    getQwebNotificationSourceUrl,
    prepareQwebNotificationIcon,
} from './qwebNotificationIcon';
import { resolveQliveOrigin } from './resolveQliveOrigin';
import {
    ensureQliveNotificationBridge,
    requestQliveNotificationPermission,
    showDeviceOnlineNotificationViaQlive,
} from './qliveNotificationBridge';

export function isPlatformDeviceOnlineNotificationSupported() {
    return typeof window !== 'undefined' && 'Notification' in window;
}

export async function requestPlatformDeviceOnlineNotificationPermission(options = {}) {
    if (!isPlatformDeviceOnlineNotificationSupported()) {
        return 'unsupported';
    }

    const qliveOrigin = resolveQliveOrigin({
        qcenterBaseUrl: options.apiClient?.defaults?.baseURL,
        qliveUrl: options.qliveUrl,
    });

    return requestQliveNotificationPermission(qliveOrigin);
}

export function getPlatformDeviceOnlineNotificationLabel(event) {
    if (event?.alias) {
        return event.alias;
    }

    if (event?.serial) {
        return event.serial;
    }

    if (event?.seqMission != null) {
        return `Mission ${event.seqMission}`;
    }

    return 'Device';
}

export function showPlatformDeviceOnlineNotification(event, options = {}) {
    if (!isPlatformDeviceOnlineNotificationSupported()) {
        return false;
    }

    const dedupKey = buildNotificationDedupKey(event);
    if (!tryAcquireNotificationSlot(dedupKey)) {
        return false;
    }

    const qliveOrigin = resolveQliveOrigin({
        qcenterBaseUrl: options.apiClient?.defaults?.baseURL,
        qliveUrl: options.qliveUrl,
    });
    const label = getPlatformDeviceOnlineNotificationLabel(event);
    const sourceUrl = options.iconUrl
        || getQwebNotificationSourceUrl(options.apiClient?.defaults?.baseURL || '');
    const icon = getPreparedQwebNotificationIconUrl(sourceUrl);

    const shown = showDeviceOnlineNotificationViaQlive({
        title: 'Device online',
        body: `${label} is now online`,
        tag: `qcenter-device-online-${dedupKey}`,
        renotify: true,
        icon,
        qliveOrigin,
    }, qliveOrigin);

    if (!shown) {
        releaseNotificationSlot(dedupKey);
        return false;
    }

    return true;
}

export function showPlatformDeviceOnlineNotifications(events, options = {}) {
    if (!Array.isArray(events)) {
        return;
    }

    events.forEach((event) => {
        showPlatformDeviceOnlineNotification(event, options);
    });
}

export function preparePlatformDeviceOnlineNotifications(options = {}) {
    const { apiClient, qliveUrl, iconUrl } = options;

    if (!apiClient) {
        return;
    }

    const qliveOrigin = resolveQliveOrigin({
        qcenterBaseUrl: apiClient.defaults?.baseURL,
        qliveUrl,
    });
    const sourceIconUrl = iconUrl || getQwebNotificationSourceUrl(apiClient.defaults?.baseURL || '');
    prepareQwebNotificationIcon(sourceIconUrl);
    ensureQliveNotificationBridge(qliveOrigin);
    requestPlatformDeviceOnlineNotificationPermission({ apiClient, qliveUrl });
}
